// Manual sections: console users (User Management), agent records and General Manager teams
// (Agent Management), a General Manager's own team, the daily transcription cap, the analytics
// dashboard and QA reports. See ../index.js for the schema.

const administration = [
  {
    id: 'users',
    group: 'Administration',
    title: 'User Management',
    lede: 'Who can sign in to the console, and which groups they are in.',
    roles: ['sAdm'],
    blocks: [
      {
        type: 'p',
        text:
          'Super Admin only, under **Administration › User Management**. Each row is one sign-in: **Username**, **Email**, **Status** (`Enabled`, `Disabled` or `Unconfirmed`) and **Groups**. The groups (SuperAdmin, Admins, QAAgent, CallAgent) decide what the person sees.',
      },
      {
        type: 'ul',
        items: [
          'Search with `Search users`, or narrow the list to one group with the `All Groups` picker.',
          'Click a column heading to sort by it. The list shows 10 at a time with `Prev` and `Next`; `Load More` fetches further users when there are more.',
        ],
      },
      { type: 'h3', text: 'Creating a user' },
      {
        type: 'steps',
        items: [
          { title: 'Open the form', text: 'Click `Create User`.' },
          {
            title: 'Fill it in',
            text:
              '**Username**, **Email Address** and a **Password** of at least 8 characters with no leading or trailing spaces. The password is temporary: they choose their own the first time they sign in.',
          },
          {
            title: 'Pick their groups',
            text:
              'Under **Assign Groups**, QAAgent is picked to start with. Hold Ctrl (Windows) or Command (Mac) to pick more than one.',
          },
          { title: 'Save', text: 'Click `Create`. If the username is taken, choose another or edit the existing user.' },
        ],
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'Call agents appear straight away',
        text:
          'Giving someone the CallAgent group, when you create them or later, also gives them an agent record, so they are listed in {{Agent Management|#agents}} before their first call. If their calls are already filed under that name, or their sign-in is already linked to an agent, the existing record is used instead.',
      },
      { type: 'h3', text: 'Changing a user' },
      {
        type: 'p',
        text: 'Open the actions menu at the end of a row:',
      },
      {
        type: 'ul',
        items: [
          '`Edit` — change their email and their groups. The username can’t be changed, and a user needs at least one group. The password is not changed here.',
          '`Set Role` — replace all their groups with one: `Super-Admin`, `Admin`, `QA-Agent` or `Call-Agent`.',
          '`Reset PW` — set a new temporary password, which they change when they next sign in.',
          '`Disable` or `Enable` — stop or allow their sign-in, after a confirmation.',
          '`Delete` — removes the sign-in for good. Type their username to confirm.',
        ],
      },
      {
        type: 'p',
        text:
          'Deleting or disabling a sign-in does not touch the calls filed under that person, or their agent record.',
      },
    ],
  },

  {
    id: 'agents',
    group: 'Administration',
    title: 'Agent Management',
    lede: 'The agents named on the calls: their role, who they report to, their email and their sign-in.',
    roles: ['sAdm'],
    blocks: [
      {
        type: 'p',
        text:
          'Under **Administration › Agent Management**, with two tabs: **Agent Emails** and **Activity Log**. An agent is listed once they have a processed call, or once a user is given the CallAgent group in {{User Management|#users}}. New users are made there, not here.',
      },
      { type: 'h3', text: 'Finding an agent' },
      {
        type: 'ul',
        items: [
          'Agents load 30 at a time, most recently active first; `Load More Agents` brings the next 30. There are no totals.',
          'The search box matches an agent’s name, email or linked sign-in.',
          {
            text: 'The filter beside it:',
            items: [
              '`All Agents` — everyone.',
              '`By General Manager…` — opens a second picker. `All General Managers` lists every General Manager; pick one to see their team, with the General Manager first.',
              '`General Managers`, `Junior Managers`, `No role` — by role.',
              '`No login`, `Has login` — whether a Call Agent user signs in as them.',
            ],
          },
          'The role and login filters narrow only the agents loaded so far, and the line under the list says so; `Load More Agents` brings in further matches.',
        ],
      },
      { type: 'h3', text: 'Reading a row' },
      {
        type: 'ul',
        items: [
          '**Agent Name** — the name on the calls. `Signs in as …` shows a sign-in with a different username; `No login` means nobody signs in as them, so nobody sees these calls under My Calls.',
          '**Role & Hierarchy** — `General Manager`, `Junior Manager`, `QA Assessor` or `No Role`, then `Reports to:` their General Manager. A General Manager with a team shows `Team: N`; click it to see who.',
          '**Email Address** — the email set here. If none is set but they have a sign-in, its email shows, marked `Login email`.',
          '**Status** — `Cognito User Active` when they can sign in; otherwise `Email set` or `No email`.',
        ],
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'No weekly emails',
        text: 'The weekly per-agent report emails are switched off, so setting an email does not send the agent anything.',
      },
      { type: 'h3', text: 'Editing an agent' },
      {
        type: 'p',
        text:
          'Click `Edit Details` (`Set Details` for an agent with no email or role yet), make the changes and `Save`. The confirmation lists everything saved.',
      },
      {
        type: 'ul',
        items: [
          '**Login** — link the Call Agent user who handles these calls when their username differs from the name on the calls, for example the login jdoe for calls under John Doe. They then see these calls under My Calls and can dispute them. A sign-in whose username already matches the agent’s name needs no link, and the window says so.',
          '**Email Address** — leave it empty to remove it. If they have a sign-in, `Use it` copies its email in.',
          '**Role** — `General Manager`, `Junior Manager`, `QA Assessor`, or none.',
          '**Reports to General Manager** — for any role except General Manager. A Junior Manager must have one; for the others it is optional.',
        ],
      },
      {
        type: 'note',
        variant: 'caution',
        tag: 'Moving a General Manager',
        text:
          'A General Manager who still has a team can’t be given another role. Move their team to another General Manager first, one agent at a time.',
      },
      { type: 'h3', text: 'Activity Log' },
      {
        type: 'p',
        text:
          'Choose an admin under **Admin** to see each agent email or role change they made: when, which agent, the email, and the role before and after.',
      },
    ],
  },

  {
    id: 'my-team',
    group: 'Administration',
    title: 'Managing your team (General Managers)',
    lede: 'How a General Manager adds agents to their team and takes them off it.',
    roles: ['callAgent', 'sAdm'],
    blocks: [
      {
        type: 'p',
        text:
          'For a Call Agent whose agent role is General Manager. On **My Calls**, click `Manage my team` to open Agent Management with only your team controls. You see the calls of the agents on your team on My Calls.',
      },
      {
        type: 'ul',
        items: [
          'Choose `My team`, `Without a GM` (agents with no General Manager) or `All agents`. Search matches names only.',
          '`Add to my team` puts an agent with no General Manager on your team.',
          '`Remove from my team` takes one of your own agents off it. A Junior Manager can’t be removed this way, because they must have a General Manager: ask an admin to move them.',
          'An agent on someone else’s team shows `On Jane Doe’s team` (with that General Manager’s name) and can’t be added. Other General Managers have no button.',
        ],
      },
      {
        type: 'p',
        text:
          'You can’t change roles, emails or sign-ins here, and you don’t see agents’ emails. A Super Admin does those in {{Agent Management|#agents}}.',
      },
    ],
  },

  {
    id: 'quota',
    group: 'Administration',
    title: 'Transcribe Quota',
    lede: 'The daily cap on how many calls get transcribed, and the work paused behind it.',
    roles: ['sAdm'],
    blocks: [
      {
        type: 'p',
        text:
          'Super Admin only, under **Administration › Transcribe Quota**; the page is headed **Call Limiter**. Tiles show today’s **Daily Call Limit**, **Used Today** and **Remaining Today**. Calls over the limit are **paused, not dropped**: they show as `Quota Stopped` on their job’s page in {{Jobs|#jobs}} and wait here under **Paused Jobs**, grouped by day for the last 7 days.',
      },
      {
        type: 'ul',
        items: [
          "**Override today's call limit** — enter a number and `Apply`. It changes **today only**.",
          '`Set to 0` stops any more transcription today.',
          "**Default daily limit** — the standing cap, saved with `Set Default`. It applies from tomorrow's midnight reset; today's limit is unaffected.",
          'Expand a job to see the calls paused under it.',
          'Tick jobs and use `Resume` to start them again, or `Deny` to drop them from the list without transcribing them. Each row also has its own `Resume` and `Deny`.',
          'Every limit change, resume and deny asks you to type confirm first.',
        ],
      },
      {
        type: 'note',
        variant: 'tip',
        tag: 'Before you resume',
        text:
          '`Resume` is unavailable while nothing is left today: raise today’s limit above the daily limit first. If the selected jobs hold more calls than are left today, you are warned; the extra calls pause again and can be resumed later.',
      },
    ],
  },

  {
    id: 'dashboard',
    group: 'Administration',
    title: 'Analytics dashboard',
    lede: 'Trends across many calls, rather than the detail of one.',
    roles: ['sAdm', 'adm'],
    blocks: [
      {
        type: 'p',
        text:
          '**Overview › Dashboard** links to the analytics dashboard: `Open Dashboard` opens it in a new tab. If it says **Dashboard Not Available**, no dashboard is configured for this site; ask your administrator.',
      },
      {
        type: 'p',
        text: "Use it for patterns across many calls. For one call's detail, use {{Search|#search}} instead.",
      },
    ],
  },

  {
    id: 'reports',
    group: 'Administration',
    title: 'QA reports',
    lede: 'A per-call, per-question QA report for a date range, with downloads.',
    roles: ['sAdm'],
    blocks: [
      {
        type: 'p',
        text: 'Super Admin only, under **Overview › Reports**.',
      },
      { type: 'h3', text: 'Generating a report' },
      {
        type: 'steps',
        items: [
          {
            title: 'Choose the dates',
            text:
              'Pick **From** and **To**, or a quick choice: `Last 5 Days`, `Last 7 Days` or `Last 30 Days`. It starts at yesterday. Days run 00:00–23:59 SAST.',
          },
          {
            title: 'Choose what goes in',
            text: 'At least one **Campaign** and one **Scorecard** are required. The rest are optional:',
            items: [
              '**Agent** — one or more agents; empty means all agents.',
              '**GUID** — one call.',
              '**Focus** — `Assessed calls only` or `Unassessed calls only`.',
              '**Sales Status** — `Sales calls only` or `Non-sales calls only`.',
              '**Dates by** — `Call date`, or `Processed date` (the date Search shows).',
            ],
          },
          {
            title: 'Generate',
            text:
              'Click `Generate Report`. Its Report Details page opens and fills in when the report is ready, usually in under a minute. It is also listed under **Past Reports** as `Processing`, then `Complete` or `Failed`.',
          },
        ],
      },
      { type: 'h3', text: 'Report Details' },
      {
        type: 'p',
        text: 'Click a report under **Past Reports** to open it. It shows the filters it was made with, then:',
      },
      {
        type: 'ul',
        items: [
          '`Download` — `Download XLSX` and `Download CSV` for the full data, `Generate Executive Summary (PDF)`, and once that exists `Download Executive Summary (PDF)`, `Regenerate PDF` and `Download Question Analysis (PDF)`. The question analysis is per campaign, so a report covering every campaign has none. A failed XLSX or CSV offers `Retry XLSX` or `Retry CSV`.',
          '**At a Glance** — call outcomes by QA score and the pass rate per question.',
          '**Ask About This Report** — type a question and `Ask`. Answers are AI-generated from this report’s data only, so check important figures against the downloads. `Clear conversation` clears it.',
          '**Preview** — the calls in a grid. Its picker can show the top or bottom 20 by QA or Compliance Score; that changes the preview only, not the downloads.',
          '`Regenerate Report` — re-runs the report on the latest call data, for example after a call has been re-scored.',
        ],
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'Reports are snapshots',
        text:
          'A report holds the data as it was when it ran. Use `Regenerate Report` to bring it up to date.',
      },
    ],
  },
];

export default administration;
